/**
 * Doctor Verification System
 * Comprehensive verification management for doctors
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  VerifiedUser as VerificationIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  FileUpload as UploadFileIcon,
  FileDownload as DownloadFileIcon,
  FolderOpen as FolderOpenIcon,
  CreateNewFolder as CreateFolderIcon,
  DriveFileMove as MoveIcon,
  DriveFileRenameOutline as RenameIcon,
  ContentCopy as CopyIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Notifications as NotificationIcon,
  Description as DocumentIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  DataUsage as DataUsageIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// API Services
import { doctorVerificationApi } from '../../services/api/doctorManagement';

const DoctorVerification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  
  // Form states
  const [verificationForm, setVerificationForm] = useState({
    mci_registration_number: '',
    rci_registration_number: '',
    insurance_provider: '',
    insurance_policy_number: '',
    clinic_registration_number: '',
    insurance_expiry: null,
    clinic_registration_expiry: null
  });

  const [documentUploads, setDocumentUploads] = useState({
    mci_certificate: null,
    rci_certificate: null,
    insurance_certificate: null,
    clinic_registration: null,
    identity_proof: null,
    address_proof: null
  });

  const verificationSteps = [
    {
      label: 'Personal Information',
      description: 'Verify your personal details',
      icon: <PersonIcon />,
      fields: ['name', 'phone', 'email', 'address']
    },
    {
      label: 'Professional Credentials',
      description: 'Upload your professional certificates',
      icon: <AssessmentIcon />,
      fields: ['mci_registration_number', 'rci_registration_number']
    },
    {
      label: 'Insurance & Registration',
      description: 'Provide insurance and clinic registration details',
      icon: <SecurityIcon />,
      fields: ['insurance_provider', 'clinic_registration_number']
    },
    {
      label: 'Document Upload',
      description: 'Upload required documents',
      icon: <UploadIcon />,
      fields: ['documents']
    },
    {
      label: 'Review & Submit',
      description: 'Review all information and submit for verification',
      icon: <CheckIcon />,
      fields: ['review']
    }
  ];

  const requiredDocuments = [
    {
      id: 'mci_certificate',
      name: 'MCI Registration Certificate',
      description: 'Medical Council of India registration certificate',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    },
    {
      id: 'rci_certificate',
      name: 'RCI Registration Certificate',
      description: 'Rehabilitation Council of India registration certificate',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    },
    {
      id: 'insurance_certificate',
      name: 'Professional Insurance Certificate',
      description: 'Professional liability insurance certificate',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    },
    {
      id: 'clinic_registration',
      name: 'Clinic Registration Certificate',
      description: 'Clinic or practice registration certificate',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    },
    {
      id: 'identity_proof',
      name: 'Identity Proof',
      description: 'Government issued identity proof (Aadhaar, PAN, etc.)',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    },
    {
      id: 'address_proof',
      name: 'Address Proof',
      description: 'Address proof document (Utility bill, Bank statement, etc.)',
      required: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: '5MB'
    }
  ];

  useEffect(() => {
    loadVerificationData();
  }, []);

  const loadVerificationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await doctorVerificationApi.getVerificationStatus();
      setVerificationData(response);
      setVerificationForm(response);
    } catch (err) {
      console.error('Error loading verification data:', err);
      setError('Failed to load verification data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    try {
      setLoading(true);
      await doctorVerificationApi.submitVerification(verificationForm);
      toast.success('Verification documents submitted successfully!');
      setShowSubmitDialog(false);
      await loadVerificationData();
    } catch (err) {
      console.error('Error submitting verification:', err);
      toast.error('Failed to submit verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (documentId, file) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: file
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'in_review': return 'warning';
      case 'rejected': return 'error';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckIcon />;
      case 'in_review': return <WarningIcon />;
      case 'rejected': return <CancelIcon />;
      case 'pending': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const getCompletionPercentage = () => {
    if (!verificationData) return 0;
    return verificationData.verification_progress || 0;
  };

  const StatCard = ({ title, value, icon, color, onClick, description }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {description && (
              <Typography variant="caption" color="text.secondary" display="block">
                {description}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const DocumentCard = ({ document, onUpload, uploadedFile }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div">
              {document.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {document.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Accepted formats: {document.acceptedFormats.join(', ')} | Max size: {document.maxSize}
            </Typography>
          </Box>
          <Chip
            label={document.required ? 'Required' : 'Optional'}
            size="small"
            color={document.required ? 'error' : 'default'}
          />
        </Box>
        
        {uploadedFile ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <CheckIcon color="success" />
            <Typography variant="body2">
              {uploadedFile.name}
            </Typography>
            <Button size="small" color="error" onClick={() => onUpload(null)}>
              Remove
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Upload Document
            <input
              type="file"
              hidden
              accept={document.acceptedFormats.map(format => `.${format.toLowerCase()}`).join(',')}
              onChange={(e) => onUpload(e.target.files[0])}
            />
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (loading && !verificationData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Doctor Verification
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Verification Status */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Verification Status</Typography>
          <Chip
            label={verificationData?.verification_status?.toUpperCase() || 'PENDING'}
            color={getStatusColor(verificationData?.verification_status)}
            icon={getStatusIcon(verificationData?.verification_status)}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Progress: {getCompletionPercentage()}%
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={getCompletionPercentage()} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verification Progress"
            value={`${getCompletionPercentage()}%`}
            icon={<VerificationIcon />}
            color="primary.main"
            description="Completion status"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Documents Uploaded"
            value={`${Object.values(documentUploads).filter(Boolean).length}/${requiredDocuments.length}`}
            icon={<DocumentIcon />}
            color="secondary.main"
            description="Required documents"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Missing Documents"
            value={verificationData?.missing_documents?.length || 0}
            icon={<WarningIcon />}
            color="warning.main"
            description="Still required"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verification Status"
            value={verificationData?.verification_status || 'Pending'}
            icon={getStatusIcon(verificationData?.verification_status)}
            color={getStatusColor(verificationData?.verification_status)}
            description="Current status"
          />
        </Grid>
      </Grid>

      {/* Verification Steps */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Verification Process
        </Typography>
        <Stepper activeStep={activeStep} orientation="horizontal">
          {verificationSteps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                icon={step.icon}
                optional={
                  index === verificationSteps.length - 1 ? (
                    <Typography variant="caption">Last step</Typography>
                  ) : null
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Current Step Content */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          {verificationSteps[activeStep]?.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {verificationSteps[activeStep]?.description}
        </Typography>

        {/* Step 1: Personal Information */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={verificationForm.name || ''}
                onChange={(e) => setVerificationForm({ ...verificationForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={verificationForm.phone || ''}
                onChange={(e) => setVerificationForm({ ...verificationForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={verificationForm.email || ''}
                onChange={(e) => setVerificationForm({ ...verificationForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                value={verificationForm.address || ''}
                onChange={(e) => setVerificationForm({ ...verificationForm, address: e.target.value })}
              />
            </Grid>
          </Grid>
        )}

        {/* Step 2: Professional Credentials */}
        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MCI Registration Number"
                value={verificationForm.mci_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, mci_registration_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RCI Registration Number"
                value={verificationForm.rci_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, rci_registration_number: e.target.value })}
              />
            </Grid>
          </Grid>
        )}

        {/* Step 3: Insurance & Registration */}
        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Provider"
                value={verificationForm.insurance_provider}
                onChange={(e) => setVerificationForm({ ...verificationForm, insurance_provider: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Policy Number"
                value={verificationForm.insurance_policy_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, insurance_policy_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Clinic Registration Number"
                value={verificationForm.clinic_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, clinic_registration_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Insurance Expiry Date"
                  value={verificationForm.insurance_expiry}
                  onChange={(date) => setVerificationForm({ ...verificationForm, insurance_expiry: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        )}

        {/* Step 4: Document Upload */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Required Documents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please upload all required documents for verification
            </Typography>
            {requiredDocuments.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                onUpload={(file) => handleFileUpload(document.id, file)}
                uploadedFile={documentUploads[document.id]}
              />
            ))}
          </Box>
        )}

        {/* Step 5: Review & Submit */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review all information before submitting for verification
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText primary="Name" secondary={verificationForm.name} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Phone" secondary={verificationForm.phone} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Email" secondary={verificationForm.email} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Professional Credentials
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText primary="MCI Number" secondary={verificationForm.mci_registration_number} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="RCI Number" secondary={verificationForm.rci_registration_number} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Insurance Provider" secondary={verificationForm.insurance_provider} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep(activeStep - 1)}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (activeStep === verificationSteps.length - 1) {
                setShowSubmitDialog(true);
              } else {
                setActiveStep(activeStep + 1);
              }
            }}
          >
            {activeStep === verificationSteps.length - 1 ? 'Submit for Verification' : 'Next'}
          </Button>
        </Box>
      </Paper>

      {/* Missing Documents */}
      {verificationData?.missing_documents && verificationData.missing_documents.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom color="error">
            Missing Documents
          </Typography>
          <List>
            {verificationData.missing_documents.map((doc, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <WarningIcon color="error" />
                </ListItemIcon>
                <ListItemText primary={doc} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit for Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to submit your verification documents? Once submitted, you cannot make changes until the review is complete.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Verification typically takes 3-5 business days. You will be notified once the review is complete.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
          <Button onClick={handleVerificationSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Submit for Verification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorVerification;


















